// Shared helpers for the "Purchases & Distribution" admin section.
// PURCHASES     -> invoices from distributors/suppliers = money WE OWE
// DISTRIBUTIONS -> invoices billed to stores            = money STORES OWE US

// Both modules use the same UI; only these labels / endpoints differ.
export const MODULES = {
  purchases: {
    key: 'purchases',
    endpoint: 'purchases',
    partyEndpoint: 'distributors',
    partyIdField: 'distributorId',
    partyNameField: 'distributorName',
    dateField: 'purchaseDate',
    tabLabel: 'Purchases',
    title: 'Purchases',
    subtitle: 'Invoices received from distributors / suppliers - money we owe them',
    partyTitle: 'Distributor',
    partyTitlePlural: 'Distributors',
    invoiceTitle: 'Purchase',
    dateLabel: 'Purchase Date',
    amountLabel: 'Purchase Amount',
    addLabel: 'Add Purchase',
    totalLabel: 'Total Purchases',
    paidLabel: 'Total Paid',
    outstandingLabel: 'Outstanding Payable',
    overdueLabel: 'Overdue',
    partialLabel: 'Partially Paid',
    emptyInvoices: 'No purchases recorded yet.',
    emptyInvoicesHint: 'Click "Add Purchase" to record your first purchase invoice.',
    emptyParties: 'No distributors added yet.',
    emptyPartiesHint: 'Distributors are created automatically when you add a purchase, or add one here.',
    // Cash bills: purchases settled in cash at the moment of purchase.
    hasCashBills: true,
    cashTabLabel: 'Cash Bills',
    cashTitle: 'Cash Bills',
    cashSubtitle: 'Bills paid in cash at the time of purchase - settled immediately',
    cashAddLabel: 'Add Cash Bill',
    cashTotalLabel: 'Total Cash Bills',
    cashEmptyInvoices: 'No cash bills recorded yet.',
    cashEmptyInvoicesHint: 'Click "Add Cash Bill" to record a bill that was paid in cash at purchase time.',
    // Distributor credit period (e.g. 30 / 45 days) used as the default due date.
    hasCreditDays: true,
  },
  distributions: {
    key: 'distributions',
    endpoint: 'distributions',
    partyEndpoint: 'stores',
    partyIdField: 'storeId',
    partyNameField: 'storeName',
    dateField: 'billingDate',
    tabLabel: 'Distribution',
    title: 'Distribution',
    subtitle: 'Invoices billed to other stores - money they owe us',
    partyTitle: 'Store',
    partyTitlePlural: 'Stores',
    invoiceTitle: 'Distribution',
    dateLabel: 'Billing Date',
    amountLabel: 'Billed Amount',
    addLabel: 'Add Distribution',
    totalLabel: 'Total Billed',
    paidLabel: 'Total Received',
    outstandingLabel: 'Outstanding Receivable',
    overdueLabel: 'Overdue',
    partialLabel: 'Partially Received',
    emptyInvoices: 'No distributions recorded yet.',
    emptyInvoicesHint: 'Click "Add Distribution" to record your first distribution bill.',
    emptyParties: 'No stores added yet.',
    emptyPartiesHint: 'Stores are created automatically when you add a distribution, or add one here.',
  },
};

export const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'paid', label: 'Paid' },
  { value: 'partial', label: 'Partially paid' },
  { value: 'unpaid', label: 'Unpaid (balance due)' },
  { value: 'overdue', label: 'Overdue' },
];

export const INVOICE_SORT_OPTIONS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'amount_desc', label: 'Highest amount' },
  { value: 'amount_asc', label: 'Lowest amount' },
  { value: 'due_asc', label: 'Due date (soonest)' },
  { value: 'due_desc', label: 'Due date (latest)' },
];

export const PARTY_SORT_OPTIONS = [
  { value: 'name_asc', label: 'Name (A-Z)' },
  { value: 'name_desc', label: 'Name (Z-A)' },
  { value: 'outstanding_desc', label: 'Highest outstanding' },
  { value: 'amount_desc', label: 'Highest total value' },
  { value: 'latest_desc', label: 'Latest invoice date' },
];

// Sort options for the invoice history inside the distributor/store popup.
export const PARTY_DETAIL_SORT_OPTIONS = [
  { value: 'newest', label: 'New to old' },
  { value: 'oldest', label: 'Old to new' },
  { value: 'due_asc', label: 'Near due date first' },
  { value: 'due_desc', label: 'Furthest due date' },
  { value: 'amount_desc', label: 'Highest amount' },
  { value: 'amount_asc', label: 'Lowest amount' },
];

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// Visual tones reused by status badges and summary cards.
export const TONES = {
  paid: { background: '#e7f8f1', color: '#0f7a5a', border: '#9fe0c9' },
  partial: { background: '#eef2ff', color: '#3730a3', border: '#c7d2fe' },
  unpaid: { background: '#fff5e6', color: '#a05a00', border: '#f6d59b' },
  overdue: { background: '#fdeaea', color: '#b42318', border: '#f4b3b0' },
  neutral: { background: '#f3f4f6', color: '#4b5563', border: '#e5e7eb' },
};

// Remaining payable / receivable balance for an invoice.
export const getBalance = (invoice) => {
  if (!invoice) return 0;
  if (invoice.balanceAmount !== undefined && invoice.balanceAmount !== null) return Number(invoice.balanceAmount) || 0;
  const amount = Number(invoice.amount) || 0;
  const paid = Number(invoice.paidAmount) || 0;
  return Math.max(amount - paid, 0);
};

export const getPaidAmount = (invoice) => {
  if (!invoice) return 0;
  return Number(invoice.paidAmount) || 0;
};

export const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '\u20B90';
  return '\u20B9' + amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

// Dates are handled as plain YYYY-MM-DD strings so the timezone never shifts a day.
export const parseDateString = (value) => {
  if (!value) return null;
  const parts = String(value).slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const day = parseInt(parts[2], 10);
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  return new Date(year, month - 1, day);
};

// "PAID" / "PARTIAL" / "UNPAID" / "OVERDUE" badge - text based, never colour only.
export const getStatusBadge = (invoice) => {
  if (invoice && invoice.isOverdue) return { label: 'OVERDUE', tone: 'overdue' };
  if (invoice && invoice.paymentStatus === 'paid') return { label: 'PAID', tone: 'paid' };
  if (invoice && invoice.paymentStatus === 'partial') return { label: 'PARTIAL', tone: 'partial' };
  return { label: 'UNPAID', tone: 'unpaid' };
};

// Due in X days / Due Today / Overdue by X days - only shown while money is due.
export const getDueInfo = (invoice) => {
  if (!invoice) return { label: '-', tone: 'neutral' };
  if (invoice.paymentStatus === 'paid' || getBalance(invoice) <= 0) return { label: 'Paid', tone: 'paid' };
  if (!invoice.dueDate) return { label: 'No due date', tone: 'neutral' };

  const days = invoice.daysUntilDue;
  if (days === null || days === undefined || !Number.isFinite(Number(days))) {
    return { label: '-', tone: 'neutral' };
  }

  const value = Number(days);
  if (value < 0) {
    const overdue = Math.abs(value);
    return { label: 'Overdue by ' + overdue + (overdue === 1 ? ' day' : ' days'), tone: 'overdue' };
  }
  if (value === 0) return { label: 'Due Today', tone: 'unpaid' };
  return { label: 'Due in ' + value + (value === 1 ? ' day' : ' days'), tone: 'unpaid' };
};

// Removes empty / "all" values so the query string only carries active filters.
export const buildQueryString = (params) => {
  const parts = [];
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === '' || value === 'all') return;
    parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(value));
  });
  return parts.length ? '?' + parts.join('&') : '';
};

export const hasActiveFilters = (filters) =>
  Boolean(
    (filters.search && filters.search.trim()) ||
    (filters.invoiceNumber && filters.invoiceNumber.trim()) ||
    (filters.partySearch && filters.partySearch.trim()) ||
    (filters.partyId && String(filters.partyId).trim()) ||
    (filters.from && String(filters.from).trim()) ||
    (filters.to && String(filters.to).trim()) ||
    (filters.status && filters.status !== 'all')
  );

export const EMPTY_INVOICE_FORM = {
  invoiceNumber: '',
  amount: '',
  paidAmount: '',
  invoiceDate: '',
  paymentStatus: 'unpaid',
  dueDate: '',
  notes: '',
};

export const formatDate = (value) => {
  const date = parseDateString(value);
  if (!date) return '-';
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const toInputDate = (value) => (value ? String(value).slice(0, 10) : '');

// Adds whole days to a YYYY-MM-DD date string (used for distributor credit
// terms, e.g. purchase date + 30 days). Returns '' when the input is invalid.
export const addDaysToDateString = (value, days) => {
  const date = parseDateString(value);
  const offset = Number(days);
  if (!date || !Number.isFinite(offset) || offset <= 0) return '';
  date.setDate(date.getDate() + offset);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return date.getFullYear() + '-' + month + '-' + day;
};

export const todayInputValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return now.getFullYear() + '-' + month + '-' + day;
};
